// frontend/src/pages/dashboard/DashboardPage.js

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet,
  Wifi,
  Users,
  TrendingUp,
  Activity,
  Shield,
  Store,
  ChevronRight,
  CreditCard,
  Clock,
  BarChart3,
  Phone,
  Plug,
  Monitor,
  ShoppingBag,
  X,
  Plus,
  Copy,
} from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useWalletStore from '../../store/walletStore';
import useVTUStore from '../../store/vtuStore';
import useBillsStore from '../../store/billsStore';
import useCommunityStore from '../../store/communityStore';
import VirtualAccountModal from '../../components/wallet/VirtualAccountModal';
import { toast } from 'react-hot-toast';

const DashboardPage = () => {
  const { user, getProfile } = useAuthStore();
  const {
    wallet,
    getWallet,
    virtualAccount,
    virtualAccountLoading,
    getVirtualAccountDetails,
  } = useWalletStore();
  const {
    transactionSummary: vtuTransactionSummary,
    getTransactionSummary: getVTUTransactionSummary,
    transactions: vtuTransactions,
    getTransactions: getVTUTransactions,
  } = useVTUStore();
  const {
    transactionSummary: billsTransactionSummary,
    getTransactionSummary: getBillsTransactionSummary,
    transactions: billsTransactions,
    getTransactions: getBillsTransactions,
  } = useBillsStore();
  const { getUserStats, userStats } = useCommunityStore();

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMarketplaceAlert, setShowMarketplaceAlert] = useState(true);
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [showVirtualAccountModal, setShowVirtualAccountModal] = useState(false);

  const hasLoadedData = useRef(false);

  // Blinking animation for marketplace alert
  useEffect(() => {
    if (!showMarketplaceAlert) return;

    const interval = setInterval(() => {
      setIsAlertVisible((prev) => !prev);
    }, 1000);

    return () => clearInterval(interval);
  }, [showMarketplaceAlert]);

  // Data loads in the background — the page renders immediately with
  // whatever the stores already have (persisted or default), and each
  // section updates in place as its own fetch resolves. Nothing here
  // blocks the initial paint.
  useEffect(() => {
    if (!hasLoadedData.current) {
      hasLoadedData.current = true;

      const loadDashboardData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([
            getProfile(),
            getWallet(),
            getVTUTransactionSummary(),
            getBillsTransactionSummary(),
            getVTUTransactions({ limit: 10 }),
            getBillsTransactions({ limit: 10 }),
            getVirtualAccountDetails(),
          ]);

          if (user?.id) {
            await getUserStats(user.id);
          }
        } catch (error) {
          console.error('Failed to load dashboard data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadDashboardData();
    }
  }, [
    getProfile,
    getWallet,
    getVTUTransactionSummary,
    getBillsTransactionSummary,
    getVTUTransactions,
    getBillsTransactions,
    getVirtualAccountDetails,
    getUserStats,
    user?.id,
  ]);

  // Combine and sort transactions whenever they change
  useEffect(() => {
    const allTransactions = [
      ...(vtuTransactions?.map((t) => ({ ...t, category: 'vtu' })) || []),
      ...(billsTransactions?.map((t) => ({ ...t, category: 'bills' })) || []),
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setRecentTransactions(allTransactions);
  }, [vtuTransactions, billsTransactions]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const quickActions = [
    { name: 'Airtime', href: '/airtime', icon: Phone, bg: 'bg-blue-50', iconColor: 'text-blue-600', hoverBg: 'hover:bg-blue-100' },
    { name: 'Data', href: '/data', icon: Wifi, bg: 'bg-purple-50', iconColor: 'text-purple-600', hoverBg: 'hover:bg-purple-100' },
    { name: 'Electricity', href: '/bills?tab=electricity', icon: Plug, bg: 'bg-yellow-50', iconColor: 'text-yellow-600', hoverBg: 'hover:bg-yellow-100' },
    { name: 'TV', href: '/bills?tab=tv', icon: Monitor, bg: 'bg-green-50', iconColor: 'text-green-600', hoverBg: 'hover:bg-green-100' },
    { name: 'Wallet', href: '/wallet', icon: Wallet, bg: 'bg-primary-50', iconColor: 'text-primary-600', hoverBg: 'hover:bg-primary-100' },
    { name: 'Community', href: '/community', icon: Users, bg: 'bg-pink-50', iconColor: 'text-pink-600', hoverBg: 'hover:bg-pink-100' },
  ];

  if (user?.role === 'ADMIN') {
    quickActions.push({
      name: 'Admin',
      href: '/admin',
      icon: Shield,
      bg: 'bg-red-50',
      iconColor: 'text-red-600',
      hoverBg: 'hover:bg-red-100',
    });
  }

  const totalTransactions =
    (vtuTransactionSummary?.airtime?.transactionCount || 0) +
    (vtuTransactionSummary?.data?.transactionCount || 0) +
    (billsTransactionSummary?.electricity?.transactionCount || 0) +
    (billsTransactionSummary?.tv?.transactionCount || 0);

  const totalSpent =
    (vtuTransactionSummary?.airtime?.totalSpent || 0) +
    (vtuTransactionSummary?.data?.totalSpent || 0) +
    (billsTransactionSummary?.electricity?.totalSpent || 0) +
    (billsTransactionSummary?.tv?.totalSpent || 0);

  const getTransactionDetails = (transaction) => {
    const type = (
      transaction.type?.toLowerCase() ||
      transaction.serviceType?.toLowerCase() ||
      transaction.billType?.toLowerCase() ||
      transaction.metadata?.type?.toLowerCase() ||
      ''
    );

    const productName = transaction.productDetails?.name?.toLowerCase() || '';
    const productType = transaction.productDetails?.type?.toLowerCase() || '';
    const network = transaction.productDetails?.network?.toLowerCase() || '';

    const hasMeterNumber = transaction.meterNumber || transaction.metadata?.meterNumber;
    const hasSmartcardNumber = transaction.smartcardNumber || transaction.metadata?.smartcardNumber;
    const recipient = transaction.recipient || '';

    const serviceInfo = type + ' ' + productName + ' ' + productType + ' ' + network;

    if (serviceInfo.includes('airtime') || type === 'airtime') {
      return { icon: Phone, bg: 'bg-blue-50', color: 'text-blue-600', name: 'Airtime' };
    } else if (serviceInfo.includes('data') || type === 'data') {
      return { icon: Wifi, bg: 'bg-purple-50', color: 'text-purple-600', name: 'Data' };
    } else if (serviceInfo.includes('electricity') || type === 'electricity' || hasMeterNumber) {
      return { icon: Plug, bg: 'bg-yellow-50', color: 'text-yellow-600', name: 'Electricity' };
    } else if (serviceInfo.includes('tv') || serviceInfo.includes('subscription') || type === 'tv_subscription' || hasSmartcardNumber) {
      return { icon: Monitor, bg: 'bg-green-50', color: 'text-green-600', name: 'TV' };
    } else {
      if (transaction.category === 'vtu') {
        if (recipient && /^\d{11}$/.test(recipient)) {
          return { icon: Phone, bg: 'bg-blue-50', color: 'text-blue-600', name: 'Airtime/Data' };
        }
      } else if (transaction.category === 'bills') {
        if (transaction.billType?.toLowerCase().includes('electricity')) {
          return { icon: Plug, bg: 'bg-yellow-50', color: 'text-yellow-600', name: 'Electricity' };
        } else if (transaction.billType?.toLowerCase().includes('tv')) {
          return { icon: Monitor, bg: 'bg-green-50', color: 'text-green-600', name: 'TV' };
        }
      }

      return { icon: Activity, bg: 'bg-gray-50', color: 'text-gray-600', name: transaction.type || 'Transaction' };
    }
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-4 md:p-5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 md:p-5 shadow-neomorph mb-3 sm:mb-4 md:mb-5 border border-primary-100/50">
          <div className="flex flex-col gap-3">
            {/* Welcome Row */}
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                  <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 truncate">
                    Welcome back, <span className="text-primary-600">{user?.firstName}</span>!
                  </h1>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 ml-3 hidden sm:block">
                  {user?.role === 'ADMIN' ? 'Admin Dashboard' : 'Ready to make some transactions today?'}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                {/* Admin Badge */}
                {user?.role === 'ADMIN' && (
                  <div className="flex items-center px-2 py-1 bg-red-50 rounded-lg shadow-neomorph-inset">
                    <Shield className="h-3 w-3 text-red-600 mr-0.5" />
                    <span className="text-[10px] font-medium text-red-700 hidden sm:inline">Admin</span>
                  </div>
                )}

                {/* Avatar */}
                <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-md shadow-primary-500/30">
                  <span className="text-white font-semibold text-sm sm:text-base">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Wallet Balance Card — compact, full virtual account details */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-4 shadow-md">
              {/* Balance Row */}
              <div className="mb-3">
                <p className="text-[10px] text-primary-100">Wallet Balance</p>
                <span className="text-2xl font-bold text-white">
                  {formatAmount(wallet?.balance || 0)}
                </span>
              </div>

              {/* Divider */}
              <div className="border-t border-white/20 my-3" />

              {/* Virtual Account — number + copy on top, full bank/account name below */}
              {virtualAccountLoading && !virtualAccount ? (
                <div className="h-12 bg-white/10 rounded-lg animate-pulse" />
              ) : virtualAccount?.accountNumber ? (
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <CreditCard className="h-3.5 w-3.5 text-white/70 flex-shrink-0" />
                      <span className="text-sm font-mono font-semibold text-white truncate">
                        {virtualAccount.accountNumber}
                      </span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(virtualAccount.accountNumber, 'Account number')}
                      className="flex items-center gap-1 text-[10px] text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 px-2 py-1 rounded flex-shrink-0"
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                  <p className="text-[10px] text-white/60 mt-1 leading-relaxed">
                    {virtualAccount.bankName} <span className="text-white/40">•</span> {virtualAccount.accountName}
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowVirtualAccountModal(true)}
                  className="w-full flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2 text-xs text-white transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <CreditCard className="h-3.5 w-3.5" />
                    Get a virtual account for instant funding
                  </span>
                  <span className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-md font-medium">
                    <Plus className="h-3 w-3" />
                    Create
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Blinking Marketplace Alert */}
        {showMarketplaceAlert && (
          <div className={`mb-3 transition-opacity duration-500 ${isAlertVisible ? 'opacity-100' : 'opacity-40'}`}>
            <Link
              to="/marketplace"
              className="block bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 shadow-neomorph border border-green-200 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-emerald-400/10 animate-pulse" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md shadow-green-500/30 animate-pulse">
                    <ShoppingBag className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-green-800">Explore Marketplace!</h3>
                    <p className="text-sm text-green-600">Buy & sell products, discover businesses</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                    Shop Now →
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowMarketplaceAlert(false);
                    }}
                    className="p-1 hover:bg-green-200 rounded-full transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-green-600" />
                  </button>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-3 mb-3 sm:mb-4">
          <div className="bg-white rounded-lg p-2 sm:p-2.5 shadow-neomorph border border-primary-100/30">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[8px] sm:text-[10px] text-gray-500 mb-0.5 truncate">Transactions</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{totalTransactions}</p>
              </div>
              <div className="p-1 bg-gradient-to-br from-blue-500 to-blue-600 rounded shadow-md flex-shrink-0 ml-0.5">
                <Activity className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2 sm:p-2.5 shadow-neomorph border border-primary-100/30">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[8px] sm:text-[10px] text-gray-500 mb-0.5 truncate">Total Spent</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{formatAmount(totalSpent)}</p>
              </div>
              <div className="p-1 bg-gradient-to-br from-purple-500 to-purple-600 rounded shadow-md flex-shrink-0 ml-0.5">
                <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2 sm:p-2.5 shadow-neomorph border border-primary-100/30">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[8px] sm:text-[10px] text-gray-500 mb-0.5 truncate">Followers</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{userStats?.followersCount || 0}</p>
              </div>
              <div className="p-1 bg-gradient-to-br from-pink-500 to-pink-600 rounded shadow-md flex-shrink-0 ml-0.5">
                <Users className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-4">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 flex items-center">
            <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 sm:gap-2.5">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="bg-white rounded-lg p-2 sm:p-3 shadow-neomorph hover:shadow-neomorph-hover transition-all text-center group border border-gray-100"
              >
                <div className="flex flex-col items-center">
                  <div className={`p-1.5 sm:p-2 ${action.bg} rounded-md shadow-neomorph-inset mb-1 group-hover:scale-110 transition-transform`}>
                    <action.icon className={`h-3 w-3 sm:h-4 sm:w-4 ${action.iconColor}`} />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">{action.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Service Usage */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center">
                <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 mr-1.5" />
                Service Usage
              </h3>

              <div className="space-y-1.5 sm:space-y-2">
                {[
                  { name: 'Airtime', amount: vtuTransactionSummary?.airtime?.totalSpent || 0, icon: Phone, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { name: 'Data', amount: vtuTransactionSummary?.data?.totalSpent || 0, icon: Wifi, color: 'text-purple-600', bg: 'bg-purple-50' },
                  { name: 'Electricity', amount: billsTransactionSummary?.electricity?.totalSpent || 0, icon: Plug, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                  { name: 'TV', amount: billsTransactionSummary?.tv?.totalSpent || 0, icon: Monitor, color: 'text-green-600', bg: 'bg-green-50' },
                ].map((service, index) => (
                  <div key={index} className={`flex items-center justify-between p-2 ${service.bg} rounded-md border border-gray-100`}>
                    <div className="flex items-center space-x-1.5 sm:space-x-2">
                      <div className="p-1 bg-white rounded-md shadow-neomorph-inset">
                        <service.icon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${service.color}`} />
                      </div>
                      <span className="text-xs sm:text-sm text-gray-600">{service.name}</span>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-800">{formatAmount(service.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
              <div className="flex items-center mb-2 sm:mb-3">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 mr-1.5" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-800">Recent Activity</h3>
                {isLoading && (
                  <span className="ml-2 text-[10px] text-gray-400">Loading...</span>
                )}
              </div>

              {isLoading && recentTransactions.length === 0 ? (
                <div className="text-center py-5">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : recentTransactions.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">No transactions yet</p>
                  <div className="flex justify-center space-x-2 mt-3">
                    <Link
                      to="/airtime"
                      className="px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-md text-xs shadow-md shadow-primary-500/30"
                    >
                      Buy Airtime
                    </Link>
                    <Link
                      to="/data"
                      className="px-2.5 py-1.5 sm:px-3 sm:py-1.5 bg-primary-50 text-primary-700 rounded-md text-xs border border-primary-200"
                    >
                      Buy Data
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="max-h-[280px] overflow-y-auto pr-1 space-y-1.5 sm:space-y-2 scrollbar-thin scrollbar-thumb-primary-200 scrollbar-track-transparent">
                  {recentTransactions.slice(0, 10).map((transaction, index) => {
                    const details = getTransactionDetails(transaction);
                    return (
                      <div key={transaction.id || index} className={`flex items-center justify-between p-2 ${details.bg} rounded-md border border-gray-100`}>
                        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                          <div className="p-1 bg-white rounded-md shadow-neomorph-inset flex-shrink-0">
                            <details.icon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${details.color}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">{details.name} Purchase</p>
                            <p className="text-[10px] text-gray-500 truncate">
                              {formatDate(transaction.createdAt || transaction.date)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <span className="text-xs sm:text-sm font-medium text-gray-800">
                            -{formatAmount(transaction.amount)}
                          </span>
                          <p className="text-[10px] text-green-600">
                            {transaction.status || 'SUCCESS'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Marketplace Card */}
        <div className="mt-3 sm:mt-4">
          <Link
            to="/marketplace/businesses"
            className="block bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-3 sm:p-4 shadow-neomorph hover:shadow-neomorph-hover transition-all border border-primary-200/50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-2.5 min-w-0">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md shadow-primary-500/30 flex-shrink-0">
                  <Store className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-800 truncate">My Businesses</h3>
                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">Manage your businesses and listings</p>
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0">
                <span className="text-[10px] sm:text-xs font-medium text-primary-600 hidden sm:inline">Explore Now</span>
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-400" />
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Virtual Account Modal */}
      {showVirtualAccountModal && (
        <VirtualAccountModal
          onClose={() => setShowVirtualAccountModal(false)}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 4px 4px 8px #d0d6e0, -4px -4px 8px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 3px 3px 6px #d0d6e0, -3px -3px 6px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2px 2px 4px #d0d6e0, inset -2px -2px 4px #ffffff;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-blink {
          animation: blink 1s ease-in-out infinite;
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 20px;
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;